import React, { useCallback } from 'react';
import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';

import {
  useAsyncInit,
  useDebouncedCallback,
  useNavOptionsSet,
} from '@hooks';
import { BaseText, ScreenTopBar, Separator } from '@components';
import {
  ChatNavigatorNavigationPropType,
  ChatNavigatorRoutePropType,
} from '@navigation';
import { useChats } from './hooks/useChats';
import { Messaging } from './components/Messaging';
import { ConversationsList } from './components/ConversationsList';
import { useChatsModel } from '@models/Chats/ChatsModel';
import type { Conversation } from '@models/Chats/ConversationModel';

const Container = styled(View)({
  flex: 1,
  bg: '$background0',
});

const ContentContainer = styled(View)({
  flex: 1,
  flexDirection: 'row',
  justifyContent: 'center',
  mx: [null, '5%'],
});

const ConversationsContainer = styled(View)({
  flex: 1,
});

const MessagingContainer = styled(View)({
  display: ['none', 'flex'],
  flex: 3,
  pb: '$7',
  pl: [null, '$8'],
});

const MessagingPlaceholderContainer = styled(View)({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
});

const PlaceholderTextContainer = styled(View)({
  px: '$9',
  py: '$4',
  borderRadius: 12,
  bg: '$background30',
});

const PlaceholderText = styled(BaseText)({
  variant: 'text.small',
  fontWeight: 'semi',
  color: '$text300',
});

type NavigationPropType = ChatNavigatorNavigationPropType<'Chats'>;
type RoutePropType = ChatNavigatorRoutePropType<'Chats'>;

export const ChatsScreen = observer(() => {
  const { t } = useTranslation();
  const nav = useNavigation<NavigationPropType>();
  const route = useRoute<RoutePropType>();
  const chats = useChatsModel();
  const { fetch, search, fetchMore, searchMore } = useChats();

  useNavOptionsSet({
    title: t('chats.title'),
  });

  useAsyncInit(() => fetch());

  useAsyncInit(() => search(), [chats.searched.searchQuery]);

  const debouncedSearchChange = useDebouncedCallback(
    (text) => chats.searched.setSearchQuery(text),
    [],
    400,
  );

  const selectConversation = useCallback((item: Conversation) => {
    nav.setParams({ conversationId: item.id });
  }, []);

  const isConversationsLoaded = !chats.list.isEmpty;
  const isConversationSelected = !!route.params?.conversationId;

  const listData = chats.searched.searchQuery
    ? chats.searched
    : chats.list;

  const fetchMoreMethod = chats.searched.searchQuery
    ? searchMore
    : fetchMore;

  return (
    <Container>
      <ScreenTopBar
        title={t('chats.title')}
        onSearchChange={debouncedSearchChange}
      />

      <Separator size="$8" />

      <ContentContainer>
        <ConversationsContainer>
          <ConversationsList
            list={listData}
            fetchMore={fetchMoreMethod}
            onPress={selectConversation}
          />
        </ConversationsContainer>

        {isConversationsLoaded && (
          <MessagingContainer>
            {isConversationSelected ? (
              <Messaging />
            ) : (
              <MessagingPlaceholderContainer>
                <PlaceholderTextContainer>
                  <PlaceholderText>
                    {t('chats.selectChat')}
                  </PlaceholderText>
                </PlaceholderTextContainer>
              </MessagingPlaceholderContainer>
            )}
          </MessagingContainer>
        )}
      </ContentContainer>
    </Container>
  );
});
